<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="start" class="mb-4">
      <v-col cols="8" lg="auto" class="d-flex align-center">
        <report-meals-select-companies />
      </v-col>
      <v-col cols="4" lg="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>
    <v-data-table
      :items="rows"
      :headers="getMobileHeaders(headers)"
      :loading="loading"
      group-by="group"
      item-key="num"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      disable-sort
      :custom-sort="keepOrder"
      mobile-breakpoint="0"
    >
      <template v-slot:group.header="{ group, headers: columns, toggle, isOpen }">
        <td :colspan="columns.length" class="group-header" @click="toggle">
          <v-icon small class="mr-2">
            {{ isOpen ? "mdi-chevron-down" : "mdi-chevron-right" }}
          </v-icon>
          <strong>{{ toLatin(group) }}</strong>
        </td>
      </template>
      <template v-slot:item.name="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ toLatin(item.name) }}
            </span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>
      <template v-slot:body.append>
        <tr class="font-weight-bold totals-row">
          <td v-if="!device.isMobile"></td>
          <td>{{ $t("total") }}</td>
          <td v-for="shift in activeShifts" :key="shift.id" class="text-right">
            {{ totalFor(shift.key) }}
          </td>
          <td class="text-right">{{ totalFor("total") }}</td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsSelectCompanies from "@/components/report/more-restaurants/fields/ReportMealsSelectCompaniesField.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import { SHIFTS } from "@/constants/shifts";

/**
 * Kartica „Jela”: šta kuhinja sprema. Paketi su već razloženi na jela i pomnoženi brojem paketa,
 * pa se jela samo sabiraju po smjenama i grupišu po `group` (redoslijed po `sort`).
 */
export default {
  name: "ReportMealsTabDishes",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    ReportMealsSelectCompanies,
  },
  data: () => ({
    dishes: [],
    loading: false,
    selectedShifts: SHIFTS.map((shift) => shift.id),
  }),
  computed: {
    selectedCompanies() {
      return this.$store.state.report.selectedMealCompanies || [];
    },
    filters() {
      return this.$store.state.report.filters;
    },
    activeShifts() {
      return SHIFTS.filter((shift) => this.selectedShifts.includes(shift.id));
    },
    /** Jela za izabrane smjene, bez redova sa 0, sortirana po grupi (`sort`) pa po nazivu. */
    rows() {
      const rows = this.dishes
        .map((dish) => {
          const row = { ...dish, total: 0 };
          SHIFTS.forEach((shift) => {
            row[shift.key] = this.selectedShifts.includes(shift.id) ? dish[shift.key] : 0;
            row.total += row[shift.key];
          });
          return row;
        })
        .filter((row) => row.total > 0);

      const searched = this.customSearch(rows, this.headers, this.filters.search);
      return searched
        .sort((a, b) => {
          const sortA = a.sort ?? Number.MAX_SAFE_INTEGER;
          const sortB = b.sort ?? Number.MAX_SAFE_INTEGER;
          if (sortA !== sortB) return sortA - sortB;
          if (a.group !== b.group) return String(a.group).localeCompare(String(b.group));
          return String(a.name).localeCompare(String(b.name));
        })
        .map((row, index) => ({ ...row, num: index + 1 }));
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meals_main"), value: "name", sortable: false },
        ...this.activeShifts.map((shift) => ({
          text: this.$t(shift.label),
          value: shift.key,
          align: "right",
          sortable: false,
        })),
        { text: this.$t("total"), value: "total", align: "right", sortable: false },
      ];
    },
    exportHeaders() {
      const [num, ...rest] = this.headers;
      return [num, { text: this.$t("group"), value: "group" }, ...rest];
    },
  },
  methods: {
    /** Redoslijed je već određen (po `sort` grupe); bez ovoga Vuetify grupe sortira abecedno. */
    keepOrder(items) {
      return items;
    },
    totalFor(key) {
      return this.rows.reduce((sum, row) => sum + (row[key] || 0), 0);
    },
    exportItems() {
      const totals = { num: "", group: "", name: this.$t("totalU") };
      [...SHIFTS.map((shift) => shift.key), "total"].forEach((key) => {
        totals[key] = this.totalFor(key);
      });
      return [
        ...this.rows.map((row) => ({
          ...row,
          group: this.toLatin(row.group),
          name: this.toLatin(row.name),
        })),
        totals,
      ];
    },
    companiesSubtitle() {
      return this.$store.state.report.mealCompanies.length === this.selectedCompanies.length
        ? this.$t("for_all_companies")
        : this.selectedCompanies
            .map((company) => this.toLatin(company.name || company.company || ""))
            .filter(Boolean)
            .join(", ");
    },
    async loadDishes() {
      if (!this.selectedCompanies.length) {
        this.dishes = [];
        return;
      }
      this.loading = true;
      this.dishes = [];
      try {
        const companies = await reportService.getMoreRestaurantsDishes(
          this.$store.state.restaurant.id,
          {
            date: this.filters.dates.from,
            date2: this.filters.dates.to,
            company: this.selectedCompanies.map((company) => company.id).join(","),
          }
        );

        const aggregated = {};
        (Array.isArray(companies) ? companies : []).forEach((company) => {
          (company.meals || []).forEach((meal) => {
            const key = `${meal.id ?? meal.name}|${meal.group ?? ""}`;
            if (!aggregated[key]) {
              aggregated[key] = {
                id: meal.id,
                name: meal.name,
                group: meal.group || this.$t("more_restaurants_report.no_group"),
                sort: meal.sort,
              };
              SHIFTS.forEach((shift) => {
                aggregated[key][shift.key] = 0;
              });
            }
            SHIFTS.forEach((shift) => {
              aggregated[key][shift.key] += meal[shift.key]?.totalQuantity || 0;
            });
          });
        });
        this.dishes = Object.values(aggregated);
      } catch (error) {
        this.dishes = [];
        this.showErrorSnackbar(this.$t("error_loading_meals"));
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.loadDishes();
      },
      deep: true,
    },
    selectedCompanies: {
      handler() {
        this.loadDishes();
      },
      immediate: true,
      deep: true,
    },
    isPrinting() {
      if (!this.isPrinting) return;
      this.printDocument({
        tables: [
          { headers: this.exportHeaders, items: this.exportItems(), showCaption: true },
        ],
        title: [this.$t("report"), this.$t("meals_main")].join(": "),
        hideDatetime: true,
        beforeTableHtml: `<h4 style="text-align: center">${this.companiesSubtitle()} ${
          this.subtitle
        }</h4>`,
      });
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("meals_main")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("meals_main"),
            headers: this.exportHeaders,
            items: this.exportItems(),
            title: [this.$t("report"), this.$t("meals_main")].join(" "),
            subtitle: this.subtitle,
          },
          {
            name: this.$t("companies"),
            headers: [
              { text: this.$t("id"), value: "id" },
              { text: this.$t("company"), value: "name" },
            ],
            items: this.selectedCompanies.map((company) => ({
              id: company.id,
              name: this.toLatin(company.name || company.company || ""),
            })),
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.group-header {
  background: rgba(0, 0, 0, 0.04);
  cursor: pointer;
}
</style>
